import type { FilterConditionDto } from '@bindoc-projects/condition-domain-lib';
import { BdCondition } from '@bindoc-projects/condition-domain-lib';

import type {
  FilterConditionPluginSpecification,
  FilterDetailedText,
} from '@bindoc-projects/filter-condition-domain-lib';
import { FilterConditionPluginImpl } from '@bindoc-projects/filter-condition-domain-lib';

export const INTUITIVE_FILE_OF_TRUTH_CONVERSION_CONDITION_ID =
  'intuitiveFotConversion';

const DISPLAY_NAME = 'Intuitive File of Truth Conversion';

export const FilterConditionIntuitiveFileOfTruthConversionPluginType: FilterConditionPluginSpecification<
  boolean,
  FilterConditionIntuitiveFileOfTruthConversionDto,
  BdIntuitiveFileOfTruthConversionCondition
> = {
  type: 'intuitiveFotConversion',
  category: 'case',
  displayName: {
    de: DISPLAY_NAME,
    en: DISPLAY_NAME,
  },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionIntuitiveFileOfTruthConversionDto,
  ): FilterConditionPluginImpl<boolean> {
    return new FilterConditionIntuitiveFileOfTruthConversionPlugin(obj.flag);
  },
  fromCondition(
    jsonObj: BdIntuitiveFileOfTruthConversionCondition,
  ): FilterConditionPluginImpl<boolean> {
    return new FilterConditionIntuitiveFileOfTruthConversionPlugin(
      jsonObj.flag,
    );
  },
  newInstance(): FilterConditionPluginImpl<boolean> {
    return new FilterConditionIntuitiveFileOfTruthConversionPlugin(true);
  },
};

export class FilterConditionIntuitiveFileOfTruthConversionPlugin extends FilterConditionPluginImpl<
  boolean,
  FilterConditionIntuitiveFileOfTruthConversionDto
> {
  static override Specification: FilterConditionPluginSpecification<
    boolean,
    FilterConditionIntuitiveFileOfTruthConversionDto,
    BdIntuitiveFileOfTruthConversionCondition
  > = FilterConditionIntuitiveFileOfTruthConversionPluginType;

  constructor(private _flag: boolean) {
    super(FilterConditionIntuitiveFileOfTruthConversionPluginType.type);
  }

  toCondition(): BdCondition {
    return new BdIntuitiveFileOfTruthConversionCondition(this._flag);
  }

  toJSON(): FilterConditionIntuitiveFileOfTruthConversionDto | null {
    return {
      type: this.type,
      flag: this._flag,
    };
  }

  toText(): string | null {
    return DISPLAY_NAME;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    if (this._flag === null || this._flag === undefined)
      return { title, items: [] };
    return { title, items: [this._flag ? 'Ja' : 'Nein'] };
  }

  toValue(): boolean {
    return this._flag;
  }

  override equals(
    other: FilterConditionIntuitiveFileOfTruthConversionPlugin,
  ): boolean {
    return super.equals(other) && this._flag === other._flag;
  }

  override valid(): boolean {
    return this._flag !== null && this._flag !== undefined;
  }

  override validationToText(): string {
    return this.valid() ? '' : 'Ja/Nein auswählen';
  }

  clone(): FilterConditionIntuitiveFileOfTruthConversionPlugin {
    return new FilterConditionIntuitiveFileOfTruthConversionPlugin(this._flag);
  }
}

export class BdIntuitiveFileOfTruthConversionCondition extends BdCondition {
  constructor(public flag: boolean) {
    super(INTUITIVE_FILE_OF_TRUTH_CONVERSION_CONDITION_ID);
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        flag: this.flag,
      },
    };
  }
}

interface FilterConditionIntuitiveFileOfTruthConversionDto {
  type: string;
  flag: boolean;
}
